import type { ReactNode } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

// ------------------------------------------------------------------
// CompactRow — story 2-143a (P10, dashboard information hierarchy).
// ------------------------------------------------------------------
//
// THE GAP THIS FILLS. `TaskCard` (story 5-2b) is deliberately a big, thumb-
// sized unit — right for a phone-first working queue where each item IS the
// screen's whole reason to exist. A dashboard's "next up"/"recently
// approved" panels are a DIFFERENT job: several REPEATED records in a small
// amount of space, scanned quickly, one line each — the same shape
// `ItemList.tsx` (story 5-1a) already established for the Items collection
// (identity + status pill + meta caption + one open action), generalised
// here so a caller is not tied to `Inspection` or the `/app/items/:id`
// route. `DataList` (story 2-138) was considered and rejected for this: it
// is a label/value `<dl>` for a single record's OWN fields ("Type:
// Routine"), not a list of repeated records each with its own identity and
// action — the wrong grammar for this content.
//
// COMPOSES, DOES NOT REINVENT: the open control is a DS `Button`
// (`iconOnly`, `min-h-touch`) rather than a raw `<button>`, so it inherits
// the shared focus ring, disabled/loading states and the accessible-name
// contract every other icon-only control in this codebase already has.
// `notice` mirrors `TaskCard`'s own `role="status"` idiom — an explanation a
// row grows (why its next action is blocked) is announced, not silently
// redrawn.
export interface CompactRowProps {
  /** The record's own identity — a site name, a report title. Required. */
  title: string;
  /** One short fact under the title: what kind of record, or its parent. */
  subtitle?: string;
  /**
   * Status/fact badges and short text, right-aligned before the open
   * control, wrapping under the title on narrow viewports. Each item is a
   * node so a caller can pass a `Badge` — the row does not decide what a
   * status looks like, only where it sits (same rule `TaskCard.meta`
   * documents).
   */
  meta?: ReactNode;
  /**
   * Makes the row's trailing control open the record. Omitted, the row has
   * no action — a row that is not openable must not look openable (the same
   * guarantee `TaskCard.onOpen` makes).
   */
  onOpen?: () => void;
  /** Announced name for the open control. Defaults to `Open <title>`. */
  openLabel?: string;
  /** An explanation the row grew. Announced politely on insertion. */
  notice?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function CompactRow({
  title,
  subtitle,
  meta,
  onOpen,
  openLabel,
  notice,
  className = '',
  'data-testid': testId,
}: CompactRowProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      className={`flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${className}`}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{title}</p>
        {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:flex-nowrap">
        {meta}
        {onOpen && (
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            className="min-h-touch shrink-0"
            icon={<ChevronRightIcon className="h-4 w-4" />}
            onClick={onOpen}
          >
            {openLabel ?? `Open ${title}`}
          </Button>
        )}
      </div>
      {notice && (
        <div role="status" className="w-full text-xs text-muted-foreground">
          {notice}
        </div>
      )}
    </div>
  );
}

/** The `divide-y` flat-card wrapper every `CompactRow` list shares — same container idiom `ItemList.tsx` already uses. */
export function CompactRowList({
  children,
  className = '',
  'data-testid': testId,
  'aria-label': ariaLabel,
}: {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
  /** Optional accessible name for the list region (e.g. `ActivityPage`'s "Activity feed" — a real second consumer, story 2-147wg). */
  'aria-label'?: string;
}): JSX.Element {
  return (
    <div
      className={`rounded-hairline border border-border bg-card overflow-hidden divide-y divide-border/40 ${className}`}
      data-testid={testId}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
}
